import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BehaviorSubject, catchError, finalize, map, Observable, of, retry, shareReplay, tap, throwError, timer } from "rxjs";
import { OrganizationService } from "app/core/auth/organization.service";
import { FormFieldOption } from "app/shared/components/form-fields/base-input/base-input.component";
import { AppConfigService } from "app/services/app-config.service";
import { ICampaign, ICampaignBilling, IPurchaseOrder, IPurchaseOrderPayload } from "./campaigs.types";
import { ISummaryResponse } from "./details/tabs/dashboard/model/ISummaryResponse";
import { ICampaignHistory } from "./details/tabs/history/model/ICampaignHistory";
import { IMonitor } from "./details/tabs/monitor/model/IMonitor";
import { DoohDspProvider, resolveDoohDspProviders } from "./utils/dooh-dsp-provider.utils";

@Injectable({
    providedIn: "root",
})
export class CampaignsService {
    private static readonly LIST_CACHE_TTL_MS: number = 15000;
    private static readonly LIST_SUMMARIES_CACHE_TTL_MS: number = 30000;
    private static readonly OPTIONS_CACHE_TTL_MS: number = 60000;
    private static readonly SUMMARY_CACHE_TTL_MS: number = 10000;
    private static readonly AUDIENCE_CACHE_TTL_MS: number = 60000;
    private static readonly TRADER_OPTIONS_CACHE_TTL_MS: number = 60000;
    private static readonly RETRY_ATTEMPTS_FOR_429: number = 2;
    private static readonly RETRY_BASE_DELAY_MS: number = 800;
    private static readonly DEFAULT_429_COOLDOWN_MS: number = 30000;
    private static readonly SUMMARY_CACHE_MAX_ENTRIES: number = 100;

    private _data: BehaviorSubject<ICampaign[]> = new BehaviorSubject<ICampaign[]>([]);
    private _campaign: BehaviorSubject<ICampaign | null> = new BehaviorSubject<ICampaign>(null);
    private _options: BehaviorSubject<any> = new BehaviorSubject({});
    private _externalAudience: BehaviorSubject<any> = new BehaviorSubject({});
    private _listCacheTimestamp: number = 0;
    private _listInFlight$: Observable<ICampaign[]> | null = null;
    private _listRetryAfter: number = 0;
    private _listSummariesCacheTimestamp: number = 0;
    private _listSummariesInFlight$: Observable<Record<string, any>> | null = null;
    private _summariesLoading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    private _optionsCacheTimestamp: number = 0;
    private _optionsInFlight$: Observable<any> | null = null;
    private _summaryCache: Map<string, { expiresAt: number; data: ISummaryResponse }> = new Map();
    private _summaryInFlight: Map<string, Observable<ISummaryResponse>> = new Map();
    private _summaryRetryAfter: Map<string, number> = new Map();
    private _spendInFlight$: Observable<Record<string, any>> | null = null;
    private _audienceCache: any[] = [];
    private _audienceCacheTimestamp: number = 0;
    private _audienceInFlight$: Observable<any[]> | null = null;
    private _audienceRetryAfter: number = 0;
    private _traderOptionsCache: FormFieldOption[] = [];
    private _traderOptionsCacheTimestamp: number = 0;
    private _traderOptionsInFlight$: Observable<FormFieldOption[]> | null = null;
    private _traderOptionsRetryAfter: number = 0;

    constructor(
        private _httpClient: HttpClient,
        private _appConfigService: AppConfigService,
        private _organizationService: OrganizationService
    ) {}

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for data
     */
    get data$(): Observable<ICampaign[]> {
        return this._data.asObservable();
    }

    get campaign$(): Observable<any> {
        return this._campaign.asObservable();
    }

    get options$(): Observable<any> {
        return this._options.asObservable();
    }

    get externalAudience$(): Observable<any> {
        return this._externalAudience.asObservable();
    }

    /**
     * Emits true while /api/v2/campaign/summaries is in flight and the
     * campaigns in `_data` have not yet been enriched with their KPI summary.
     * The list view uses this to render skeleton placeholders on the per-row
     * KPI cells instead of misleading zeros.
     */
    get summariesLoading$(): Observable<boolean> {
        return this._summariesLoading.asObservable();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Get data
     */
    getData(): Observable<any> {
        return this._httpClient.get("/api/v1/dashboards/analytics").pipe(
            tap((response: any) => {
                this._data.next(response);
            })
        );
    }

    show(id: string): Observable<ICampaign> {
        return this._httpClient.get<ICampaign>(`/api/v2/campaign/${id}`).pipe(
            tap((response: ICampaign) => {
                this._campaign.next(response);
            })
        );
    }
    showScheduleRecommendations(id: string): Observable<any[]> {
        // Solo para obtener week_stats de v1, retorna directamente el array
        return this._httpClient.get<ICampaign>(`/api/v1/campaign/${id}`).pipe(map((campaign) => campaign?.metadata?.week_stats || []));
    }

    list(forceReload: boolean = false): Observable<ICampaign[]> {
        const now = Date.now();
        const hasFreshCache =
            this._data.value.length > 0 &&
            now - this._listCacheTimestamp < CampaignsService.LIST_CACHE_TTL_MS;

        if (!forceReload && hasFreshCache) {
            return of(this._data.value);
        }

        if (!forceReload && this._listRetryAfter > now) {
            return of(this._data.value);
        }

        if (this._listInFlight$) {
            return this._listInFlight$;
        }

        this._listInFlight$ = this._httpClient
            .get<ICampaign[]>("/api/v2/campaign")
            .pipe(
                this.retry429WithBackoff<ICampaign[]>(),
                tap((response: ICampaign[]) => {
                    this._data.next(response);
                    this._listCacheTimestamp = Date.now();
                    this._listRetryAfter = 0;
                    // Fire-and-forget: KPIs arrive after first render via separate endpoint
                    // so a slow/degraded MediaSmart never blocks /campaigns from loading.
                    this._mergeSummariesAsync();
                }),
                catchError((error: HttpErrorResponse) => {
                    if (error?.status === 429) {
                        this._listRetryAfter = Date.now() + CampaignsService.DEFAULT_429_COOLDOWN_MS;
                    }
                    return this._data.value.length > 0
                        ? of(this._data.value)
                        : throwError(() => error);
                }),
                finalize(() => {
                    this._listInFlight$ = null;
                }),
                shareReplay(1)
            );

        return this._listInFlight$;
    }

    /**
     * Pulls the DSP summary map (campaign_id -> summary) and merges it into the
     * already-emitted campaign list. Never throws to subscribers; failures and
     * cache-cold "warming" responses are tolerated silently — campaigns just
     * render without KPIs until the next refresh.
     */
    private _mergeSummariesAsync(): void {
        const now = Date.now();
        const hasFresh =
            now - this._listSummariesCacheTimestamp < CampaignsService.LIST_SUMMARIES_CACHE_TTL_MS;

        if (hasFresh || this._listSummariesInFlight$) {
            return;
        }

        // Signal subscribers (list view) to show skeleton KPIs until the
        // backend responds — empty `{}` from a cold cache still counts as
        // "done loading" because the SPA can't tell the difference.
        this._summariesLoading.next(true);

        this._listSummariesInFlight$ = this._httpClient
            .get<Record<string, any>>("/api/v2/campaign/summaries")
            .pipe(
                tap((map) => {
                    this._listSummariesCacheTimestamp = Date.now();
                    if (!map || typeof map !== "object") {
                        return;
                    }
                    const current = this._data.value;
                    if (!current?.length) {
                        return;
                    }
                    const merged = current.map((c) => {
                        const summary = map[c.id];
                        // Only merge a real, non-empty summary. A null or empty {} (cold
                        // or partial DSP response) is truthy enough to break the
                        // `!campaign.summary` skeleton/zero check, so keep the row as-is.
                        return summary && typeof summary === "object" && Object.keys(summary).length > 0
                            ? { ...c, summary }
                            : c;
                    });
                    this._data.next(merged);
                }),
                catchError(() => of({})),
                finalize(() => {
                    this._listSummariesInFlight$ = null;
                    this._summariesLoading.next(false);
                }),
                shareReplay(1)
            );

        this._listSummariesInFlight$.subscribe();
    }

    save(data: any, isStrategy?: boolean): Observable<any> {
        if (isStrategy) {
            return this._httpClient.post<any>(`/api/v1/campaign/${data.parent.id}/strategy`, data);
        } else {
            return this._httpClient.post<any>("/api/v2/campaign", data);
        }
    }

    update(data: any, id: string, isStrategy?: boolean): Observable<ICampaign> {
        if (isStrategy) {
            return this._httpClient.put<ICampaign>(`/api/v1/campaign/${data.parent.id}/strategy/${id}`, data);
        } else {
            return this._httpClient.put<ICampaign>(`/api/v2/campaign/${id}`, data);
        }
    }

    delete(id: string): Observable<any> {
        return this._httpClient.delete<ICampaign>(`/api/v1/campaign/${id}`).pipe(
            catchError((error) => {
                console.error("Error in campaign deletion API call:", error);
                return throwError(() => error);
            })
        );
    }

    /**
     * Bulk delete. The API answers 200 with `{deleted, failed: []}` and, when some campaign couldn't
     * be deleted, 502 with `{message, deleted, failed: [{id, error}]}` — after the interceptor that
     * 502 is an ApiError with the payload in `body`. Either way the deleted ids leave the list cache
     * right away (the next `data$` emission would paint them again otherwise) and the failed stay.
     */
    bulkDelete(ids: string[]): Observable<{ deleted: string[]; failed: Array<{ id: string; error?: string }> }> {
        return this._httpClient.request<any>("delete", `/api/v2/campaign`, { body: { ids } }).pipe(
            map((response) => ({
                deleted: Array.isArray(response?.deleted) ? response.deleted.map((id: unknown) => String(id)) : [...ids],
                failed: Array.isArray(response?.failed) ? response.failed : [],
            })),
            tap((result) => this._removeCampaignsFromList(result.deleted)),
            catchError((error) => {
                const body = error?.body ?? error?.error;
                if (Array.isArray(body?.deleted)) {
                    this._removeCampaignsFromList(body.deleted.map((id: unknown) => String(id)));
                }
                console.error("Error in bulk campaign deletion:", error);
                return throwError(() => error);
            })
        );
    }

    private _removeCampaignsFromList(ids: string[]): void {
        if (!ids.length) {
            return;
        }
        const removed = new Set(ids);
        this._data.next(this._data.value.filter((campaign) => !removed.has(String(campaign.id))));
    }

    deleteStrategy(cid: string, id: string, strategy?: any): Observable<any> {
        // Send only the state change. The full form payload used to travel
        // here too, dragging along untouched fields — including whatever an
        // unhydrated deals selector held (deals: []), which wiped real PMP
        // deals upstream on the way out.
        return this._httpClient.put<any>(`/api/v1/campaign/${cid}/strategy/${id}`, { state: "deleted" });
    }

    options(): Observable<any> {
        const now = Date.now();
        const currentOptions = this._options.value;
        const hasOptions = currentOptions && Object.keys(currentOptions).length > 0;
        const hasFreshCache = hasOptions && now - this._optionsCacheTimestamp < CampaignsService.OPTIONS_CACHE_TTL_MS;

        if (hasFreshCache) {
            return of(currentOptions);
        }

        if (this._optionsInFlight$) {
            return this._optionsInFlight$;
        }

        this._optionsInFlight$ = this._httpClient
            .get("/api/v1/campaign/options")
            .pipe(
                this.retry429WithBackoff<any>(),
                tap((response: any) => {
                    this._options.next(response);
                    this._optionsCacheTimestamp = Date.now();
                }),
                finalize(() => {
                    this._optionsInFlight$ = null;
                }),
                shareReplay(1)
            );

        return this._optionsInFlight$;
    }

    /**
     * DSPs a new DOOH campaign of the active organization may be created on (`dooh_dsp_providers` /
     * `dooh_dsp_provider` of the options, shared cache included; default first, see
     * `resolveDoohDspProviders`). What decides whether a DOOH Direct or a standard-DSP DOOH entry point
     * is offered at all. Never errors: without the options it answers DOOH Direct alone, the same as an
     * API that doesn't send the fields, and the API still refuses a DSP the organization may not use.
     */
    doohDspProviders(): Observable<DoohDspProvider[]> {
        return this.options().pipe(
            map((options) => resolveDoohDspProviders(options)),
            catchError(() => of<DoohDspProvider[]>(["perion"]))
        );
    }

    /**
     * Default DSP of a new DOOH campaign of the active organization: the first of `doohDspProviders()`.
     * Kept for the callers that only need the default; it never errors either.
     */
    doohDspProvider(): Observable<DoohDspProvider> {
        return this.doohDspProviders().pipe(map((providers) => providers[0]));
    }

    getTraderOptions(): Observable<FormFieldOption[]> {
        const now = Date.now();
        const hasFreshCache =
            this._traderOptionsCache.length > 0 &&
            now - this._traderOptionsCacheTimestamp < CampaignsService.TRADER_OPTIONS_CACHE_TTL_MS;

        if (hasFreshCache) {
            return of(this._traderOptionsCache);
        }

        if (this._traderOptionsRetryAfter > now) {
            return of(this._traderOptionsCache);
        }

        if (this._traderOptionsInFlight$) {
            return this._traderOptionsInFlight$;
        }

        this._traderOptionsInFlight$ = this._httpClient.get<unknown>("/api/users/campaigns").pipe(
            this.retry429WithBackoff<unknown>(),
            map((res) => this.normalizeUsersWithCampaignsToOptions(res)),
            tap((options) => {
                this._traderOptionsCache = options;
                this._traderOptionsCacheTimestamp = Date.now();
                this._traderOptionsRetryAfter = 0;
            }),
            catchError((error: HttpErrorResponse) => {
                if (error?.status === 429) {
                    this._traderOptionsRetryAfter = Date.now() + CampaignsService.DEFAULT_429_COOLDOWN_MS;
                }
                return of(this._traderOptionsCache);
            }),
            finalize(() => {
                this._traderOptionsInFlight$ = null;
            }),
            shareReplay(1)
        );

        return this._traderOptionsInFlight$;
    }

    private normalizeUsersWithCampaignsToOptions(res: unknown): FormFieldOption[] {
        const items = this.normalizeUsersWithCampaignsResponse(res);
        const orgId = this.currentOrganizationId();
        const filtered = orgId
            ? items.filter((item: { organization_id?: string }) => String(item?.organization_id ?? "") === String(orgId))
            : items;
        return filtered.map((item: { id?: string; name?: string; email?: string; username?: string }) => ({
            key: String(item.id ?? ""),
            value: (item.name?.trim() || item.email || item.username || item.id) ?? "",
        }));
    }

    private normalizeUsersWithCampaignsResponse(res: unknown): any[] {
        if (Array.isArray(res)) return res;
        if (res && typeof res === "object") {
            const r = res as Record<string, unknown>;
            if (Array.isArray(r.data)) return r.data;
            if (Array.isArray(r.users)) return r.users;
        }
        return [];
    }

    externalAudience(id: string): Observable<any> {
        return this._httpClient
            .get("assets/mocke2e/externalAudience.json") // Ruta al archivo mock
            .pipe(
                map((response: any) => {
                    // Asumiendo que el JSON tiene un array de objetos con 'id' y 'name'
                    return response.map((item: any) => ({
                        key: item.id, // Mapear 'id' a 'key'
                        value: item.name, // Mapear 'name' a 'value'
                    }));
                }),
                tap((mappedResponse: any) => this._externalAudience.next(mappedResponse))
            );
    }

    /**
     * Gasto real acumulado de una campaña, o `null` si no se puede saber.
     *
     * Sale de `total_spend`, que **solo** expone el summary masivo del listado: el summary individual
     * (`/analytics/summary`) trae hoy y ayer por métrica, no el acumulado. Si la campaña ya está en la
     * lista cargada se responde con lo que hay y no se pide nada.
     *
     * Lo usa el asistente de DTS para prerrellenar la inversión con lo gastado en vez de con el
     * presupuesto planificado (Santiago, 21/09): en una medición parcial la diferencia entre los dos
     * es directamente el coste por visita atribuida.
     */
    campaignSpend(campaignId: string): Observable<number | null> {
        const cached = this._data.value?.find((c: any) => String(c?.id) === String(campaignId)) as
            | { summary?: unknown }
            | undefined;
        const fromList = this.readSpend(cached?.summary);
        if (fromList !== null) {
            return of(fromList);
        }

        if (!this._spendInFlight$) {
            this._spendInFlight$ = this._httpClient.get<Record<string, any>>("/api/v2/campaign/summaries").pipe(
                catchError(() => of({} as Record<string, any>)),
                finalize(() => {
                    this._spendInFlight$ = null;
                }),
                shareReplay(1)
            );
        }

        return this._spendInFlight$.pipe(map((summaries) => this.readSpend(summaries?.[String(campaignId)])));
    }

    private readSpend(summary: unknown): number | null {
        const spend = Number((summary as { total_spend?: unknown })?.total_spend);

        return isNaN(spend) || spend <= 0 ? null : spend;
    }

    summary(campaignId: string): Observable<ISummaryResponse> {
        return this.getSummaryWithCache(`campaign:${campaignId}`, `/api/v1/campaign/${campaignId}/analytics/summary`);
    }

    summaryStrategy(campaignId: string, strategyId: string): Observable<ISummaryResponse> {
        return this.getSummaryWithCache(
            `strategy:${campaignId}:${strategyId}`,
            `/api/v1/campaign/${campaignId}/analytics/summary/${strategyId}`
        );
    }

    private getSummaryWithCache(cacheKey: string, url: string): Observable<ISummaryResponse> {
        const now = Date.now();
        const cached = this._summaryCache.get(cacheKey);
        const retryAfter = this._summaryRetryAfter.get(cacheKey) ?? 0;

        if (cached && cached.expiresAt > now) {
            return of(cached.data);
        }

        // Circuit breaker: while backend is throttling this resource, avoid hammering.
        if (retryAfter > now) {
            if (cached) {
                return of(cached.data);
            }

            return of({} as ISummaryResponse);
        }

        const inFlight = this._summaryInFlight.get(cacheKey);
        if (inFlight) {
            return inFlight;
        }

        const request$ = this._httpClient
            .get<ISummaryResponse>(url)
            .pipe(
                this.retry429WithBackoff<ISummaryResponse>(),
                tap((data) => {
                    this._summaryCache.set(cacheKey, {
                        data,
                        expiresAt: Date.now() + CampaignsService.SUMMARY_CACHE_TTL_MS,
                    });
                    this.evictSummaryCacheIfNeeded();
                    this._summaryRetryAfter.delete(cacheKey);
                }),
                catchError((error: HttpErrorResponse) => {
                    if (error?.status === 429) {
                        this._summaryRetryAfter.set(cacheKey, Date.now() + CampaignsService.DEFAULT_429_COOLDOWN_MS);
                    }
                    return throwError(() => error);
                }),
                finalize(() => {
                    this._summaryInFlight.delete(cacheKey);
                }),
                shareReplay(1)
            );

        this._summaryInFlight.set(cacheKey, request$);
        return request$;
    }

    private retry429WithBackoff<T>() {
        return (source: Observable<T>): Observable<T> =>
            source.pipe(
                retry({
                    count: CampaignsService.RETRY_ATTEMPTS_FOR_429,
                    delay: (error: HttpErrorResponse, retryCount: number) =>
                        error?.status === 429
                            ? timer(CampaignsService.RETRY_BASE_DELAY_MS * retryCount)
                            : throwError(() => error),
                })
            );
    }

    private evictSummaryCacheIfNeeded(): void {
        if (this._summaryCache.size <= CampaignsService.SUMMARY_CACHE_MAX_ENTRIES) {
            return;
        }

        const firstKey = this._summaryCache.keys().next().value as string | undefined;
        if (!firstKey) {
            return;
        }

        this._summaryCache.delete(firstKey);
        this._summaryRetryAfter.delete(firstKey);
    }

    publishers(campaignId: string, from: string | null, to: string | null): Observable<any> {
        let url = `/api/v1/campaign/${campaignId}/analytics/publishers`;

        // Añadir parámetros de fecha solo si no son null
        const params: string[] = [];
        if (from) {
            params.push(`from=${from}`);
        }
        if (to) {
            params.push(`to=${to}`);
        }

        // Concatenar parámetros a la URL si existen
        if (params.length) {
            url += `?${params.join("&")}`;
        }

        return this._httpClient.get<any>(url);
    }

    monitor(campaignId: string, exchange: string = "all"): Observable<IMonitor> {
        const url = `/api/v1/campaign/${campaignId}/monitor?exchange=${exchange}`;
        return this._httpClient.get<IMonitor>(url);
    }

    strategyMonitor(strategyId: string, exchange: string = "all"): Observable<IMonitor> {
        const url = `/api/v1/campaign/strategy/${strategyId}/monitor?exchange=${exchange}`;
        return this._httpClient.get<IMonitor>(url);
    }

    history(campaignId: string): Observable<ICampaignHistory[]> {
        const url = `/api/v1/campaign/${campaignId}/history`;
        return this._httpClient.get<ICampaignHistory[]>(url);
    }

    strategyHistory(strategyId: string): Observable<ICampaignHistory[]> {
        const url = `/api/v1/campaign/strategy/${strategyId}/history`;
        return this._httpClient.get<ICampaignHistory[]>(url);
    }

    /**
     * Drops the freshness of the audience list so the next read hits the server. The old list stays as
     * the fallback for a failed read. Audiences are created through other services, so nothing else
     * would ever tell this cache that a new one exists.
     */
    invalidateAudienceCache(): void {
        this._audienceCacheTimestamp = 0;
        this._audienceRetryAfter = 0;
    }

    // ------------------------------------------------------------------ Plan 31 · F8.6 billing / purchase orders

    /** Monthly billing of the campaign (`month` = YYYY-MM; the server defaults to the current month). */
    billing(campaignId: string, month?: string | null): Observable<ICampaignBilling> {
        const query = month ? `?month=${encodeURIComponent(month)}` : "";
        return this._httpClient.get<ICampaignBilling>(`/api/v2/campaign/${campaignId}/billing${query}`);
    }

    purchaseOrders(campaignId: string): Observable<IPurchaseOrder[]> {
        return this._httpClient.get<IPurchaseOrder[]>(`/api/v2/campaign/${campaignId}/purchase-orders`);
    }

    /**
     * Purchase orders live at organization level and are append-only: a change in flight is a new
     * order carrying `supersedes_id`. Requires `pricing.manage` (403 otherwise).
     */
    createPurchaseOrder(payload: IPurchaseOrderPayload): Observable<IPurchaseOrder> {
        const organizationId = this.currentOrganizationId();
        return this._httpClient.post<IPurchaseOrder>(`/api/v2/organizations/${organizationId}/purchase-orders`, payload);
    }

    /** Organization selected by a super-admin, else the organization of the logged user. */
    private currentOrganizationId(): string | null {
        return this._appConfigService.getSelectedOrganizationId() ?? this._organizationService.getOrganization()?.id ?? null;
    }

    audienceList() {
        const now = Date.now();
        const hasFreshCache =
            this._audienceCache.length > 0 &&
            now - this._audienceCacheTimestamp < CampaignsService.AUDIENCE_CACHE_TTL_MS;

        if (hasFreshCache) {
            return of(this._audienceCache);
        }

        if (this._audienceRetryAfter > now) {
            return of(this._audienceCache);
        }

        if (this._audienceInFlight$) {
            return this._audienceInFlight$;
        }

        this._audienceInFlight$ = this._httpClient.get<any[]>(`/api/v1/audience?for_targeting=true`).pipe(
            this.retry429WithBackoff<any[]>(),
            tap((audiences) => {
                this._audienceCache = audiences ?? [];
                this._audienceCacheTimestamp = Date.now();
                this._audienceRetryAfter = 0;
            }),
            catchError((error: HttpErrorResponse) => {
                if (error?.status === 429) {
                    this._audienceRetryAfter = Date.now() + CampaignsService.DEFAULT_429_COOLDOWN_MS;
                }
                return of(this._audienceCache);
            }),
            finalize(() => {
                this._audienceInFlight$ = null;
            }),
            shareReplay(1)
        );

        return this._audienceInFlight$;
    }

    public createAudience(data: any): Observable<any> {
        const formData = this.convertToFormData(data);
        return this._httpClient.post<any>("/api/v1/audience", formData).pipe(tap(() => this.invalidateAudienceCache()));
    }

    public createGeoList(data: any): Observable<any> {
        const formData = this.convertToFormData(data);
        return this._httpClient.post<any>("/api/v1/geo-list", formData);
    }

    geoListList() {
        return this._httpClient.get<any>("/api/v1/geo-list");
    }

    private convertToFormData(data: any): FormData {
        const formData = new FormData();
        for (const key in data) {
            if (data.hasOwnProperty(key)) {
                if (Array.isArray(data[key])) {
                    data[key].forEach((item: any) => formData.append(`${key}[]`, item));
                } else {
                    formData.append(key, data[key]);
                }
            }
        }
        return formData;
    }

    public duplicateCampaign(campaignId: string, data: any): Observable<any> {
        return this._httpClient.post<any>(`/api/v2/campaign/${campaignId}/duplication`, data);
    }

    public duplicateStrategy(strategyId: string, data: any): Observable<any> {
        return this._httpClient.post<any>(`/api/v2/campaign/strategy/${strategyId}/duplication`, data);
    }

    public contactUs(data: any): Observable<any> {
        const formData = this.convertToFormData(data);
        return this._httpClient.post<any>("/api/v1/contact-us", formData);
    }

    public setStrategy(data: any) {
        this._campaign.next(data);
    }

    public getSessionData(campaignId: string) {
        return this._httpClient.put(`/api/v1/campaign/${campaignId}`, {}, { responseType: "blob" });
    }
}
